import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api } from '@/api/client'
import { PageHeader } from '@/app/shell'
import { Badge, Button, Card } from '@/ui/primitives'
import { DataTable, type Coloana } from '@/ui/data-table'
import { NewMenu } from '@/ui/new-menu'

type Grup = {
  id: number; name: string; destination: string | null; hotel: string | null
  supplier: string | null; begin_date: string | null; end_date: string | null
  capacity: number | null; signups_open: boolean; responsible: string | null
  rezervari: number; pax: number; inscrieri: number; inscrieri_noi: number
}
type Sugestie = {
  destination: string; begin_date: string | null; supplier: string | null
  rezervari: number; pax: number; reservation_ids: number[]
}

const zi = (s: string | null) =>
  s ? new Date(s).toLocaleDateString('ro-RO', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'

export function Grupuri() {
  const nav = useNavigate()
  const qc = useQueryClient()
  const [aratSugestii, setAratSugestii] = useState(false)

  const { data, isPending } = useQuery({
    queryKey: ['grupuri'], queryFn: () => api.get<{ groups: Grup[]; total: number }>('/groups/'),
  })
  const sugestii = useQuery({
    queryKey: ['grupuri-sugestii'],
    queryFn: () => api.get<{ sugestii: Sugestie[] }>('/groups/sugestii'),
  })

  const creeaza = useMutation({
    mutationFn: (s: Sugestie) => api.post<{ group: { id: number } }>('/groups/', {
      name: `${s.destination} ${s.begin_date ?? ''}`.trim(),
      destination: s.destination, begin_date: s.begin_date, supplier: s.supplier,
      capacity: s.pax, reservation_ids: s.reservation_ids,
    }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['grupuri'] })
      qc.invalidateQueries({ queryKey: ['grupuri-sugestii'] })
      if (r?.group?.id) nav(`/rezervari/grupuri/${r.group.id}`)
    },
  })

  const coloane: Coloana<Grup>[] = [
    { cheie: 'name', antet: 'Grup', sortabil: true, valoare: (g) => g.name,
      randare: (g) => (
        <span>
          <span className="font-medium">{g.name}</span>
          <span className="block text-[12px] text-muted">
            {[g.destination, g.hotel, g.supplier].filter(Boolean).join(' · ') || '—'}
          </span>
        </span>
      ) },
    { cheie: 'plecare', antet: 'Plecare', sortabil: true, valoare: (g) => g.begin_date ?? '',
      randare: (g) => <span className="tnum">{zi(g.begin_date)}</span> },
    { cheie: 'rezervari', antet: 'Rezervari', sortabil: true, aliniat: 'dreapta', valoare: (g) => g.rezervari,
      randare: (g) => <span className="tnum">{g.rezervari}</span> },
    { cheie: 'pax', antet: 'Persoane', sortabil: true, aliniat: 'dreapta', valoare: (g) => g.pax,
      randare: (g) => (
        <span className="tnum">
          {g.pax}{g.capacity ? <span className="text-muted"> / {g.capacity}</span> : null}
        </span>
      ) },
    { cheie: 'inscrieri', antet: 'Inscrieri', sortabil: true, aliniat: 'dreapta', valoare: (g) => g.inscrieri,
      randare: (g) => (g.inscrieri
        ? <span className="tnum">{g.inscrieri}{g.inscrieri_noi ? <span className="text-warn"> ({g.inscrieri_noi} noi)</span> : null}</span>
        : <span className="text-muted">—</span>) },
    { cheie: 'stare', antet: 'Stare', sortabil: true, valoare: (g) => String(g.signups_open),
      randare: (g) => g.signups_open ? <Badge tone="good">deschise</Badge> : <Badge tone="neutral">inchise</Badge> },
  ]

  const lista = sugestii.data?.sugestii ?? []

  return (
    <>
      <PageHeader title="Grupuri"
        count={data ? `${data.total} grupuri` : undefined}
        actions={
          <>
            <Button onClick={() => setAratSugestii((v) => !v)}>
              {aratSugestii ? 'Ascunde sugestiile' : `Sugestii (${lista.length})`}
            </Button>
            <Button variant="primary" onClick={() => nav('/nou/grup')}>Grup nou</Button>
            <NewMenu />
          </>
        } />

      <div className="px-8 py-6 space-y-5">
        {aratSugestii && (
          <Card className="p-5">
            <h3 className="text-[14.5px] font-medium">Grupuri care exista deja in rezervari</h3>
            <p className="mt-1 text-[13px] text-muted">
              Aceeasi destinatie, aceeasi zi de plecare, acelasi furnizor — cel putin 3 rezervari
              care nu sunt inca intr-un grup. Un clic le aduna.
            </p>
            <div className="mt-4 space-y-2">
              {lista.length === 0 && <p className="text-[13.5px] text-muted">Nimic de grupat acum.</p>}
              {lista.map((s, i) => (
                <div key={i} className="flex items-center gap-4 py-2 border-t border-line">
                  <span className="tnum text-[13.5px] w-32">{zi(s.begin_date)}</span>
                  <span className="flex-1 min-w-0 truncate text-[14px]">
                    {s.destination}
                    {s.supplier && <span className="text-muted"> · {s.supplier}</span>}
                  </span>
                  <span className="tnum text-[13.5px] text-muted whitespace-nowrap">
                    {s.rezervari} rez · {s.pax} pax
                  </span>
                  <Button onClick={() => creeaza.mutate(s)} disabled={creeaza.isPending}>
                    Fa grupul
                  </Button>
                </div>
              ))}
            </div>
          </Card>
        )}

        <DataTable
          randuri={data?.groups ?? []} coloane={coloane} cheie={(g) => g.id} incarca={isPending}
          sortImplicit={{ cheie: 'plecare', sens: 'desc' }}
          peRand={(g) => nav(`/rezervari/grupuri/${g.id}`)}
          cauta={(g, t) => `${g.name} ${g.destination ?? ''} ${g.supplier ?? ''}`.toLowerCase().includes(t)}
          placeholderCautare="Cauta grup, destinatie, furnizor…"
          gol={{ titlu: 'Niciun grup',
                 indiciu: 'Apasa „Sugestii" — plecarile de grup exista deja in rezervari.' }}
        />
      </div>
    </>
  )
}
